const AUTH_COOKIE_NAME = "jwtToken";
const AUTH_COOKIE_MAX_AGE_SECONDS = 86400;
const tokenListeners = new Set();

export function subscribeAuthTokenChanges(listener) {
  tokenListeners.add(listener);
  return () => tokenListeners.delete(listener);
}

function notifyTokenChange() {
  tokenListeners.forEach(listener => listener());
}

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === "token" || event.key === "authSession" || event.key === null) notifyTokenChange();
  });
}

function getCookieValue(name) {
  if (typeof document === "undefined") return null;
  const cookiePrefix = `${name}=`;
  const cookie = document.cookie
    .split(";")
    .map(value => value.trim())
    .find(value => value.startsWith(cookiePrefix));

  if (!cookie) return null;

  return decodeURIComponent(cookie.slice(cookiePrefix.length));
}

function getSecureCookieAttribute() {
  if (typeof window === "undefined" || !window.location || window.location.protocol !== "https:") {
    return "";
  }

  return "; secure";
}

export function getStoredAuthToken() {
  if (typeof localStorage !== "undefined") {
    const localStorageToken = localStorage.getItem("token");
    if (localStorageToken && localStorageToken !== "undefined" && localStorageToken !== "null") {
      return localStorageToken;
    }
  }

  const cookieToken = getCookieValue(AUTH_COOKIE_NAME);
  if (cookieToken && typeof localStorage !== "undefined") {
    localStorage.setItem("token", cookieToken);
  }

  return cookieToken;
}

export function persistAuthToken(token) {
  const changed = getStoredAuthToken() !== token;
  localStorage.token = token;

  if (typeof document !== "undefined") {
    document.cookie = `jwtToken=${encodeURIComponent(token)}; path=/; max-age=${AUTH_COOKIE_MAX_AGE_SECONDS}; samesite=lax${getSecureCookieAttribute()}`;
  }
  if (changed) notifyTokenChange();
}

export function getStoredAuthSession() {
  try {
    return JSON.parse(localStorage.getItem("authSession")) || null;
  } catch (error) {
    return null;
  }
}

export function getStoredAuthEpoch() { return localStorage.getItem("eeAuthEpoch"); }

export function persistAuthSession(session) {
  if (session && session.id && Number.isFinite(session.idleExpiresAt)) {
    localStorage.setItem("authSession", JSON.stringify(session));
  } else localStorage.removeItem("authSession");
  notifyTokenChange();
}

// Decoding is for coordination only; authorization remains verified by the API.
export function decodeStoredToken(token = getStoredAuthToken()) {
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(decodeURIComponent(Array.prototype.map.call(atob(payload), c => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`).join("")));
  } catch (error) {
    return null;
  }
}

export function getStoredAuthIdentity() {
  const token = getStoredAuthToken();
  const decoded = decodeStoredToken(token);
  return decoded ? JSON.stringify([decoded.id, decoded.sid, decoded.sv, decoded.schoolId, decoded.role, decoded.activeContextKey]) : token;
}

export function removeAuthToken() {
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem("token");
    localStorage.removeItem("authSession");
    localStorage.setItem("eeAuthEpoch", `${Date.now()}-${Math.random()}`);
  }

  if (typeof document !== "undefined") {
    document.cookie = `${AUTH_COOKIE_NAME}=; path=/; max-age=0; samesite=lax${getSecureCookieAttribute()}`;
  }
  notifyTokenChange();
}
